import {AxiosError} from "axios";
import {AntennaSignal as SMSIcon, Bin as DeleteIcon, Plus as SendSMSIcon} from "iconoir-react";
import {useEffect, useState} from "react";
import {Dropdown} from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import Row from "react-bootstrap/Row";
import Table from "react-bootstrap/Table";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {ApiConfig} from "../api/admin/ApiConfig";
import {SMSApi, SMSMessage, SortColumn, SortDirection} from "../api/admin/generated";
import {ActionsDropdown} from "../common/ActionsDropdown";
import {ClearSelection} from "../common/ClearSelection";
import {ConfirmDialog} from "../common/ConfirmDialog";
import {InvertSelection} from "../common/InvertSelection";
import {Pager} from "../common/Pager";
import {PageTitle} from "../common/PageTitle";
import {ReloadButton} from "../common/ReloadButton";
import {RenderAlert} from "../common/RenderAlert";
import {RowsPerPage} from "../common/RowsPerPage";
import {Truncate} from "../common/Truncate";
import {useSpinnerStatusContext} from "../hook/UseSpinnerStatusContext";
import {DateUtils} from "../utils/DateUtils";
import {logger} from "../utils/Logger";
import {Utils} from "../utils/Utils";
import {SMSSendMessagePopup} from "./SMSSendMessagePopup";

/*
 * REST api for accessing the back-end
 */
const smsApi = new SMSApi(ApiConfig.config());

interface ColumnProps {
    value?: string
    children?: React.ReactNode
    className? : string
    maxLength? : number
}

const Column = ({value, children, className, maxLength = 60}: ColumnProps) => {
    return (
        <td className={"user-select-all " + className}>{children}<Truncate value={value} maxLength={maxLength}/></td>
    )
}

export const SMSQueue = () => {
    /**
     * Navigate is used to navigate to the login page if the user is no longer authenticated or to some other page
     * if the dialog is closed
     */
    const navigate = useNavigate();
    /**
     * The SMS messages to show in the table. The messages are fetched from the back-end.
     */
    const [messages, setMessages] = useState<SMSMessage[]>([]);
    /**
     * The total number of SMS messages in the queue. The number is fetched from the back-end.
     */
    const [queueSize, setQueueSize] = useState<number>(0);
    /**
     * The error message if some general error occurred. The error is fetched from the back-end.
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The index of the first page to show
     */
    const [currentPage, setCurrentPage] = useState<number>(1);
    /**
     * The max number of domains to show
     */
    const [rowsPerPage, setRowsPerPage] = useState<number>(40);
    /**
     * The selected messages. The user selects the messages. The user can select multiple messages.
     */
    const [selectedMessages, setSelectedMessages] = useState<SMSMessage[]>([]);
    /**
     * If true, the items will be reloaded from the back-end
     */
    const [forceReload, setForceReload] = useState<boolean>(false);
    /**
     * If true, the "confirm deletion" modal dialog will be shown
     */
    const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
    /**
     * If true, the "send SMS" modal popup will be shown
     */
    const [showSendSMSPopup, setShowSendSMSPopup] = useState<boolean>(false);
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();

    /**
     * i18next translation hook
     */
    const {t} = useTranslation();

    useEffect(() =>
    {
        logger.debug("Load queue size")

        // reset state when we load new data
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        smsApi.sMSControllerGetSMSQueueSize()
            .then(response => {
                setQueueSize(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting queue size", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            });
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        t
    ]);

    useEffect(() =>
    {
        logger.debug("Load messages")

        // reset state when we load new data
        setForceReload(false);

        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        smsApi.sMSControllerGetSMSMessages(SortColumn.Created, SortDirection.Asc, (currentPage - 1) * rowsPerPage, rowsPerPage)
            .then(response => {
                setMessages(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error getting messages", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
            })
    }, [navigate,
        forceReload,
        setSpinnerStateCounter,
        currentPage,
        rowsPerPage,
        t
    ]);

    const onChangeSelect = (checked : boolean, message : SMSMessage) =>
    {
        setSelectedMessages(checked ?
            [...selectedMessages, message] :
            selectedMessages.filter(q => q.id !== message.id)
        );
    }

    const invertSelection = () => {
        setSelectedMessages([...messages].filter(q => !selectedMessages.includes(q)));
    }

    const deleteSelected = () =>
    {
        if (setSpinnerStateCounter !== undefined) {
            setSpinnerStateCounter(count => count + 1);
        }

        smsApi.sMSControllerDeleteSMS(selectedMessages.map(m => m.id))
            .then(() => {
                setForceReload(true);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error deleting queue items", err);
                Utils.redirectToLoginIfNotAuthenticated(err, navigate);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
                if (setSpinnerStateCounter !== undefined) {
                    setSpinnerStateCounter(count => count - 1);
                }
                setShowDeleteConfirm(false);
                setSelectedMessages([]);
            })
    }

    /**
     * Is called when the SMS Send Message Dalog is closed
     */
    const onShowSendSMSPopupDalogClose = () =>
    {
        setShowSendSMSPopup(false);
        setForceReload(true);
    }

    return (
        <>
            <ConfirmDialog title={t("common.confirm")}
                           bodyText={t("sms.are-you-sure-delete-sms")}
                           confirmButtonTitle={t("common.delete")}
                           onClickConfirmEvent={deleteSelected}
                           showConfirm={showDeleteConfirm}
                           setShowConfirm={setShowDeleteConfirm}
            />

            {showSendSMSPopup && <SMSSendMessagePopup onClose={onShowSendSMSPopupDalogClose} />}

            <Row>
                <div className="d-flex flex-row align-items-center border-bottom pb-2 mb-4">
                    <div className="d-flex justify-content-start">
                        <PageTitle title={t("sms.title")} icon={<SMSIcon/>}/>
                    </div>
                    <div className="d-flex flex-fill justify-content-end">
                        <ReloadButton onClickReload={() => setForceReload(true)} />
                    </div>
                </div>
            </Row>

            <Row>
                <Col>
                    <RenderAlert message={errorMessage} setMessage={setErrorMessage} tooltipPlacement={"bottom"}/>
                </Col>
            </Row>

            <Row>
                <div className="d-flex flex-row align-items-center mb-4">
                    <div className="d-flex flex-fill justify-content-end align-items-center">
                        <ActionsDropdown>
                            <Dropdown.Item onClick={() => setShowSendSMSPopup(true)}>
                                <SendSMSIcon className="text-success"/> {t("sms.send-sms")}
                            </Dropdown.Item>
                        </ActionsDropdown>
                    </div>
                </div>
            </Row>

            <Row className="h-100 w-100 overflow-auto">
                <Col>
                    <Table className="sticky sticky-x" bordered hover>
                        <thead className="sticky-top">
                        <tr className="text-nowrap">
                            {selectedMessages.length === 0 ?
                                <>
                                    <th className="th-w-min"><InvertSelection invertSelectionEvent={invertSelection}/></th>
                                    <th>{t("sms.id")}</th>
                                    <th>{t("sms.phone-number")}</th>
                                    <th>{t("sms.created")}</th>
                                    <th>{t("sms.last-try")}</th>
                                    <th>{t("sms.last-error")}</th>
                                </>
                                :
                                <th colSpan={6}>
                                    <Navbar className="bg-body-tertiary ps-2">
                                        {/* Add icon to first column which will clear the selection when clicked */}
                                        <ClearSelection clearSelectionEvent={() => setSelectedMessages([])} checked={true}/>

                                        <Navbar.Text className="ms-2">{selectedMessages.length} selected</Navbar.Text>

                                        <div className="vr ms-4 me-2"></div>

                                        <Nav>
                                            <Nav.Link onClick={() => setShowDeleteConfirm(true)}>
                                                <DeleteIcon className="text-danger"/> {t("sms.delete-selected")}
                                            </Nav.Link>
                                        </Nav>
                                    </Navbar>
                                </th>
                            }
                        </tr>
                        </thead>
                        <tbody>
                        {messages?.map((m: SMSMessage) => (
                            <tr key={m.id}>
                                <Column className="td-w-min">
                                    <Form.Check
                                        type="checkbox"
                                        className="me-0"
                                        checked={selectedMessages.includes(m)}
                                        onChange={(e) => onChangeSelect(e.target.checked, m)}
                                        inline
                                    />
                                </Column>
                                <Column value={m.id}/>
                                <Column value={m.phoneNumber}/>
                                <Column value={DateUtils.timestampToDateStringShort(m.created)}/>
                                <Column value={DateUtils.timestampToDateStringShort(m.lastTry)}/>
                                <Column value={m.lastError} maxLength={100}/>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>

            <Row className="mt-4 align-items-baseline">
                <div className="col-4 d-flex">
                    <div className="d-flex justify-content-start align-items-center d-none d-md-inline-flex me-4">
                        {queueSize} {t("common.results")}
                    </div>

                    <div className="d-flex justify-content-start align-items-center">
                        <RowsPerPage rowsPerPage={rowsPerPage} setRowsPerPage={setRowsPerPage}
                                     setCurrentPage={setCurrentPage}/>
                    </div>
                </div>
                <div className="col-4">
                    <div className="d-flex justify-content-center">
                        <Pager itemCount={queueSize}
                               currentPage={currentPage}
                               setCurrentPage={setCurrentPage}
                               maxItemsPerPage={rowsPerPage}
                        />
                    </div>
                </div>
            </Row>
        </>
    );
}